<script setup lang="ts">
import { useCorporateNavigation } from '@corporate/composables'
import { useFocusTrap } from '@corporate/composables/useFocusTrap'
import { FooterSocialList } from '@gro-up/ui/components'
import { CustomNuxtLink, MenuButton, Typography, DomainLink, SvgComponent } from '@gro-up/ui/primitives'
import { useScrollLock } from '@vueuse/core'
import { onMounted, onUnmounted, watch, useTemplateRef } from 'vue'

const model = defineModel<boolean>({ default: false })

const isLocked = useScrollLock(import.meta.client ? document.body : null)
const asideRef = useTemplateRef<HTMLElement | null>('asideRef')
const { homeLinks, primaryLinks, socialMedia, search, domain, topNav } = useCorporateNavigation()
useFocusTrap(model, asideRef)

watch(model, val => (isLocked.value = val), { immediate: true })

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    model.value = false
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Transition name="overlay">
    <div
      v-if="model"
      class="fixed inset-0 z-40 bg-[#3C3C3C] mix-blend-multiply"
      aria-hidden="true"
      @click.self="model = false" />
  </Transition>

  <Transition name="aside">
    <div
      v-if="model"
      ref="asideRef"
      class="aside-panel"
      role="dialog"
      aria-label="Subnavigatie"
      aria-modal="true">
      <div class="flex justify-end items-center py-4 px-8 mb-3 gap-[30px]">
        <CustomNuxtLink
          v-if="search"
          :to="search.url"
          class="text-primary-20 hover:text-accent-100 transition-colors"
          aria-label="Zoeken">
          <SvgComponent icon="search" class="h-[18px] w-[18px]" />
        </CustomNuxtLink>
        <MenuButton
          variant="white"
          :is-open="true"
          aria-label="Sluit menu"
          @click="model = false" />
      </div>

      <div class="px-8 pb-10 flex flex-col gap-[50px] relative">
        <img src="/illustrations/navigation-gruppets.webp" width="173" height="256" class="absolute top-0 right-10 pointer-events-none" alt="" >

        <ul v-if="primaryLinks" class="flex flex-col gap-4">
          <li v-for="(item, index) in primaryLinks" :key="`aside-primaryLink-${index}` + item.title" class="text-white">
            <DomainLink
              v-if="item.type === 'corporateSubhomeNavigationLink'"
              :domain="item.domain ?? 'head-office'"
              variant="header-6"
              :url="item.url ?? ''"
              :active="item.domain === domain?.domain"
              inverted />
            <CustomNuxtLink v-else :to="item.url">
              <Typography as="span" variant="header-6" class="font-bold transition-colors hover:text-accent-100">{{
                item.title
              }}</Typography>
            </CustomNuxtLink>
          </li>
        </ul>

        <ul v-if="homeLinks?.length" class="flex flex-col gap-3">
          <li
            v-for="(item, index) in homeLinks"
            :key="`aside-homeLink-${index}` + item.title"
            class="text-white header-6 tracking-tight transition-colors hover:text-accent-100">
            <CustomNuxtLink :to="item.url">
              <Typography as="span" variant="body-medium" class="text-primary-20">{{ item.title }}</Typography>
            </CustomNuxtLink>
          </li>
        </ul>

        <FooterSocialList
          class="text-primary-20"
          variant="currentColor"
          :socials="socialMedia"
          aria-label="Sociale media"
          data-component="FooterSocialList" />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
@reference "@gro-up/ui/main.css";

.aside-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  width: 473px;
  max-width: 90vw;
  overflow-y: auto;
  @apply bg-primary-90 pt-12;
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s var(--ease-smooth);
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.aside-enter-active,
.aside-leave-active {
  transition: transform 0.5s cubic-bezier(0.76, 0, 0.24, 1);
}

.aside-enter-from,
.aside-leave-to {
  transform: translateX(100%);
}
</style>

